import { useCallback, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { DevicePermission } from '../ports/DeviceNotifier';
import { useDeviceNotifier, useNotificationRepository } from '../container';
import { keys } from '../query-keys';

/**
 * Whether this device may show notifications, and the way to ask. The request
 * of 7 October 2026, section 1.
 */
export function useDevicePermission(): {
  permission: DevicePermission;
  request: () => Promise<DevicePermission>;
} {
  const notifier = useDeviceNotifier();
  const [permission, setPermission] = useState<DevicePermission>(() => notifier.permission());

  const request = useCallback(async () => {
    const answer = await notifier.request();
    setPermission(answer);
    return answer;
  }, [notifier]);

  return { permission, request };
}

/**
 * Shows, on this device, the notifications management marked for push.
 *
 * Asked of the server while the app is open, once a minute and the moment the
 * live stream says this person's bell changed, since it is filed with their
 * other notification queries. Each row is shown once: the newest one seen is
 * remembered, and the next request asks for what came after it. Nothing is
 * asked on a device that has not allowed notifications.
 */
export function useDeviceNotifications(): void {
  const notifier = useDeviceNotifier();
  const repo = useNotificationRepository();
  // From now: what was written before this device opened the app is in the centre.
  const after = useRef<string>(new Date().toISOString());
  // And never one twice, whatever the server answers with.
  const shown = useRef(new Set<string>());

  useQuery({
    queryKey: [...keys.notifications, 'push'],
    queryFn: async () => {
      const feed = await repo.push(after.current);
      for (const row of feed.items) {
        if (shown.current.has(row.id)) continue;
        shown.current.add(row.id);
        notifier.show({ id: row.id, title: row.title, body: row.text });
      }
      // Where the server says to ask from next: exact, where a row's own time may not be.
      if (feed.next !== null) after.current = feed.next;
      return feed.items.length;
    },
    enabled: notifier.permission() === 'granted',
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}
